import { memo, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import type { Dispatch } from 'redux';
import cn from 'classnames';

import { KnmSignatureInfoStatusesEnum } from 'APIPackage/KnmModule/enums/KnmSignatureInfoStatusesEnum';
import { IKnm } from 'APIPackage/KnmModule/interfaces/IKnm';
import { IKnmSignatureInfo } from 'APIPackage/KnmModule/interfaces/IKnmSignatureInfo';
import { DATE_FORMAT_LONG_FNS } from 'CorePackage/MainModule/constants/dateFormats';
import { KNM_SIGN_INFO } from 'CorePackage/MainModule/routes/routes';
import { DatePicker } from 'UIPackage/MainModule/components/DatePicker';
import { TextInput } from 'UIPackage/MainModule/components/TextInput';
import { formatDate } from 'CorePackage/MainModule/common/formatDate';
import { Section } from 'UIPackage/FormModule/components/Section';
import { Row } from 'UIPackage/FormModule/components/Row';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { RootActions } from 'CorePackage/MainModule/types/RootActions';
import { addNoticeAction, removeNoticeAction } from 'CorePackage/NotificationModule/actions/NoticeActions';
import { NoticeTypesEnum } from 'CorePackage/NotificationModule/enums/NoticeTypesEnum';
import { MessageTypesEnum } from 'CorePackage/NotificationModule/enums/MessageTypesEnum';

import styles from './KnmSignInfo.module.scss';
import { KnmSignInfoMoreAccordion } from './KnmSignInfoMoreAccordion';
import { getSignStatus } from '../common/getSignStatus';
import { getValidityPeriod } from '../common/getValidityPeriod';

interface IKnmSignInfoProps {
  title: string;
  notificationText: string;
  signaturesInfo: IKnm['signatureInfo'] | undefined;
  isPm?: boolean;
}

export const KnmSignInfo = memo(({ title, notificationText, signaturesInfo, isPm }: IKnmSignInfoProps) => {
  const dispatch = useDispatch<Dispatch<RootActions>>();
  const [isShown, setIsShown] = useState(false);

  useEffect(() => {
    if (signaturesInfo?.some(item => item.status === KnmSignatureInfoStatusesEnum.INVALID)) {
      if (signaturesInfo?.length === 1 && !isShown) {
        dispatch(
          addNoticeAction({
            noticeType: NoticeTypesEnum.WARNING,
            messageType: MessageTypesEnum.KNM_SIGNED_INVALID,
            renderContent: 'Подпись не валидна, требуется переподписание',
            id: MessageTypesEnum.KNM_SIGNED_INVALID,
          }),
        );
        setIsShown(true);
      } else if (isShown && signaturesInfo?.some(item => item.status === KnmSignatureInfoStatusesEnum.VALID)) {
        dispatch(removeNoticeAction(MessageTypesEnum.KNM_SIGNED_INVALID));
      }
    }
  }, [dispatch, isShown, signaturesInfo]);

  return (
    <Section title={title} id={KNM_SIGN_INFO}>
      {!!signaturesInfo && signaturesInfo.length > 0 ? (
        signaturesInfo.map((signatureInfo: IKnmSignatureInfo) => (
          <Row isListItem key={signatureInfo.signatureId}>
            {signatureInfo.status !== KnmSignatureInfoStatusesEnum.IN_PROCESS && (
              <Row>
                <FieldBlock label="Результат подписания">
                  <TextInput
                    id="signatureResult"
                    name="signatureResult"
                    value={`Паспорт ${isPm ? 'ПМ' : 'КНМ'} ${
                      signatureInfo.status === KnmSignatureInfoStatusesEnum.INVALID ? 'не ' : ''
                    }подписан усиленной КЭП${
                      signatureInfo?.signedDate
                        ? ` ${formatDate({ date: signatureInfo?.signedDate, dateFormat: DATE_FORMAT_LONG_FNS })}`
                        : ''
                    }`}
                    disabled
                  />
                </FieldBlock>
              </Row>
            )}
            <Row>
              <FieldBlock label="Статус подписи" color="black" placementLabel="left" labelSize="col-4">
                <TextInput
                  className="shared-row-field"
                  id="signatureStatus"
                  name="signatureStatus"
                  value={getSignStatus(signatureInfo.status, signatureInfo.errorMessage)}
                  disabled
                />
              </FieldBlock>
            </Row>

            {signatureInfo?.status !== KnmSignatureInfoStatusesEnum.IN_PROCESS && (
              <>
                {signatureInfo?.certInfo?.certNumber && (
                  <Row>
                    <FieldBlock label="Сертификат" color="black" placementLabel="left" labelSize="col-4">
                      <TextInput
                        className="shared-row-field"
                        id="signatureCertNumber"
                        name="signatureCertNumber"
                        value={signatureInfo?.certInfo?.certNumber}
                        disabled
                      />
                    </FieldBlock>
                  </Row>
                )}
                {signatureInfo?.certInfo?.issuer?.name && (
                  <Row>
                    <FieldBlock label="Владелец" color="black" placementLabel="left" labelSize="col-4">
                      <TextInput
                        className="shared-row-field"
                        id="signatureIssuerName"
                        name="signatureIssuerName"
                        value={signatureInfo?.certInfo?.issuer?.name}
                        disabled
                      />
                    </FieldBlock>
                  </Row>
                )}
                {signatureInfo?.certInfo?.subject?.role && (
                  <Row>
                    <FieldBlock label="Должность" color="black" placementLabel="left" labelSize="col-4">
                      <TextInput
                        className="shared-row-field"
                        id="signatureRole"
                        name="signatureRole"
                        value={signatureInfo?.certInfo?.subject?.role}
                        disabled
                      />
                    </FieldBlock>
                  </Row>
                )}
                {(signatureInfo?.certInfo?.validFrom || signatureInfo?.certInfo?.validTo) && (
                  <Row>
                    <FieldBlock label="Действителен с" color="black" placementLabel="left" size="half">
                      <DatePicker
                        className="shared-row-field"
                        id="signatureCertValidFrom"
                        name="signatureCertValidFrom"
                        value={formatDate({
                          date: signatureInfo?.certInfo?.validFrom,
                          dateFormat: DATE_FORMAT_LONG_FNS,
                        })}
                        disabled
                      />
                    </FieldBlock>
                    <FieldBlock label="Действителен по" color="black" placementLabel="left" size="half">
                      <DatePicker
                        className="shared-row-field"
                        id="signatureCertValidTo"
                        name="signatureCertValidTo"
                        value={formatDate({ date: signatureInfo?.certInfo?.validTo, dateFormat: DATE_FORMAT_LONG_FNS })}
                        disabled
                      />
                    </FieldBlock>
                  </Row>
                )}
              </>
            )}
            {(signatureInfo?.certInfo?.versionNumber ||
              signatureInfo?.certInfo?.certNumber ||
              signatureInfo?.certInfo?.argorithmName ||
              signatureInfo?.certInfo?.issuer?.name ||
              signatureInfo?.certInfo?.validFrom ||
              signatureInfo?.certInfo?.validTo ||
              signatureInfo?.certInfo?.subject?.name ||
              signatureInfo?.certInfo?.subjectPublicKeyInfo) && (
              <KnmSignInfoMoreAccordion>
                <Row>
                  <FieldBlock label="Версия" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="versionNumber"
                      name="versionNumber"
                      value={signatureInfo?.certInfo?.versionNumber}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Серийный номер" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="certNumber"
                      name="certNumber"
                      value={signatureInfo?.certInfo?.certNumber}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Алгоритм подписывания ЦС" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="argorithmName"
                      name="argorithmName"
                      value={signatureInfo?.certInfo?.argorithmName}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Имя издателя" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="issuerName"
                      name="issuerName"
                      value={signatureInfo?.certInfo?.issuer?.name}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Период действия" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="validityPeriod"
                      name="validityPeriod"
                      value={getValidityPeriod(signatureInfo?.certInfo?.validFrom, signatureInfo?.certInfo?.validTo)}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock label="Имя субъекта" color="black" placementLabel="left" labelSize="col-4">
                    <TextInput
                      className="shared-row-field"
                      id="subjectName"
                      name="subjectName"
                      value={signatureInfo?.certInfo?.subject?.name}
                      disabled
                    />
                  </FieldBlock>
                </Row>
                <Row>
                  <FieldBlock
                    label=" Данные открытого ключа субъекта"
                    color="black"
                    placementLabel="left"
                    labelSize="col-4"
                  >
                    <TextInput
                      className="shared-row-field"
                      id="subjectPublicKeyInfo"
                      name="subjectPublicKeyInfo"
                      value={signatureInfo?.certInfo?.subjectPublicKeyInfo}
                      disabled
                    />
                  </FieldBlock>
                </Row>
              </KnmSignInfoMoreAccordion>
            )}
          </Row>
        ))
      ) : (
        <p className={cn('shared-group-label', styles.NoSignatureInfoNotification)}>{notificationText}</p>
      )}
    </Section>
  );
});
